import type {
  Need,
  Skill,
  SkillCategory,
  SkillNeedTemplate
} from '@lmc/ts-common'
import { Button, Edit, TableOLD, ThreeDots } from '@lmc/ui-kit'
import { Dropdown } from 'antd'
import type React from 'react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getCategories, getSkill } from './utils'

const NA_COLOR = '#595959'

const StyledNAText = styled.span`
  color: ${NA_COLOR};
`

const StyledValidityText = styled.span`
  color: #595959;
  font-weight: 400;
`

const StyledBulletList = styled.ul`
  margin: 0;
  padding-left: 18px;
  list-style-type: disc;
`

const renderNAOrValue = (
  value: string | null | undefined
): React.ReactElement =>
  value ? <span>{value}</span> : <StyledNAText>N/A</StyledNAText>

const getNeedNames = (
  ids: string[],
  needOptions: Need[] | undefined
): string[] => {
  if (!ids?.length || !needOptions?.length) return []
  return ids
    .map((id) => needOptions.find((need) => need.id === id)?.name)
    .filter((name): name is string => !!name)
}

const StyledActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledDotsButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const StyledEditButton = styled(Button)`
  gap: 2px;
  padding: 6px 15px;
  margin-right: 8px;
`

type Props = {
  skillOptions: Skill[]
  needOptions: Need[]
  categoriesOptions: SkillCategory[]
  skillNeedTemplates: SkillNeedTemplate[]
  setTemplateDetail: (template: SkillNeedTemplate) => void
  onDeleteTemplate: (template: SkillNeedTemplate) => void
  allowCreateEditTemplates?: boolean
}
export const SkillNeedTemplateTable: FC<Props> = ({
  skillNeedTemplates,
  skillOptions,
  needOptions,
  categoriesOptions,
  setTemplateDetail,
  onDeleteTemplate,
  allowCreateEditTemplates
}) => {
  const { t } = useTranslation()

  return (
    <TableOLD
      columns={[
        {
          key: 'skills',
          title: t(`skillsAndNeeds.table.skill`) as string,
          cellType: 'custom' as const,
          width: '25%',
          renderCell: (value: unknown) => renderNAOrValue(value as string)
        },
        {
          key: 'training',
          title: t(`skillsAndNeeds.table.training`) as string,
          cellType: 'custom' as const,
          width: '25%',
          renderCell: (_value: unknown, row: object) => {
            const trainingName = (row as Record<string, unknown>)
              ._trainingName as string | null
            const validityText = (row as Record<string, unknown>)
              ._validityText as string | null
            if (!trainingName) return <StyledNAText>N/A</StyledNAText>
            return (
              <span>
                {trainingName}
                {validityText && (
                  <>
                    <br />
                    <StyledValidityText>({validityText})</StyledValidityText>
                  </>
                )}
              </span>
            )
          }
        },
        {
          key: 'coveredNeeds',
          title: t(`skillsAndNeeds.table.coveredNeeds`) as string,
          cellType: 'custom' as const,
          width: '25%',
          renderCell: (value: unknown) => {
            const names = value as string[]
            if (!names?.length) return <StyledNAText>N/A</StyledNAText>
            return (
              <StyledBulletList>
                {names.map((name, i) => (
                  <li key={i}>{name}</li>
                ))}
              </StyledBulletList>
            )
          }
        },
        {
          key: 'category',
          title: t(`skillsAndNeeds.table.category`) as string,
          cellType: 'custom' as const,
          width: 150,
          renderCell: (value: unknown) => renderNAOrValue(value as string)
        },
        ...(allowCreateEditTemplates
          ? [
              {
                key: 'actions',
                title: t('skillsAndNeeds.actionsColumn') as string,
                cellType: 'custom' as const,
                width: 150,
                renderCell: (_value: unknown, row: object) => {
                  const template = (row as Record<string, unknown>)
                    ._template as SkillNeedTemplate
                  return (
                    <StyledActionsCell
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <StyledEditButton
                        variant="default"
                        onClick={() => setTemplateDetail(template)}
                      >
                        <Edit />
                        {t('skillsAndNeeds.editNeed')}
                      </StyledEditButton>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'delete',
                              danger: true,
                              label: t('skillsAndNeeds.deleteSkillAction'),
                              onClick: () => onDeleteTemplate(template)
                            }
                          ],
                          style: { borderRadius: 20 }
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        align={{ offset: [-4, 16] }}
                      >
                        <StyledDotsButton>
                          <ThreeDots />
                        </StyledDotsButton>
                      </Dropdown>
                    </StyledActionsCell>
                  )
                }
              }
            ]
          : [])
      ]}
      onRow={() => ({})}
      rows={skillNeedTemplates.map((skill) => {
        return {
          key: skill?.id,
          id: skill?.id,
          skills: getSkill(skill.skill as string, skillOptions) || null,
          training: skill?.trainingName || null,
          _trainingName: skill?.trainingName || null,
          _validityText: skill.trainingValidFor
            ? t('skillsAndNeeds.form.validForMonths', {
                count: skill.trainingValidFor
              })
            : null,
          coveredNeeds: getNeedNames(
            (skill?.needs as string[]) || [],
            needOptions
          ),
          category:
            getCategories(
              (skill?.categories as string[]) || [],
              categoriesOptions
            ) || null,
          mandatory: skill.mandatory
            ? t(`skillsAndNeeds.table.yes`)
            : t(`skillsAndNeeds.table.no`),
          _template: skill,
          actions: null
        }
      })}
    />
  )
}

export default SkillNeedTemplateTable
